import { Box, Theme } from "@outschool/backpack";
import { Container, Loading } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry } from "@outschool/ui-utils";
import React, { useEffect, useState } from "react";

import { useStickyHeader } from "../../../hooks/useStickyHeader";
import { useMembershipRequirement } from "../../subscriptions/hooks/useMembershipRequirement";
import ActivityEnrollableTimes from "../activityEnrollableTimes/ActivityEnrollableTimes";
import { AvailableTimesWrapper } from "../availableTimes/AvailableTimesWrapper";
import useTutoringFlow from "../hooks/useTutoringFlow";
import { ReviewsSection } from "../sections/Reviews";
import TeacherSection from "../sections/TeacherSection";
import ClassDetailsNav from "../subcomponents/ClassDetailsNav";
import DesktopActivityHeader from "../subcomponents/DesktopActivityHeader";
import { hasOneOnOneAvailabilities } from "../utils";
import { DesktopContainerProps } from "./ClassDetailsMainBody";
import JoinMembershipDesktopActivityCard from "./desktopActivityCards/JoinMembershipDesktopActivityCard";
import UnlockedDesktopActivityCard from "./desktopActivityCards/UnlockedDesktopActivityCard";

const Overview = lazyWithRetry(
  () => import(/* webpackChunkName: "overview" */ "../sections/Overview"),
);
const ActivityDetailDescription = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "activity-detail-description" */ "../sections/ActivityDetailDescription"
    ),
);
const LearningGoals = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "learning-goals" */ "../sections/LearningGoals"
    ),
);
const SyllabusSummary = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "syllabus-summary" */ "../sections/SyllabusSummary"
    ),
);
const OtherDetails = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "other-details" */ "../sections/OtherDetails"),
);
const Disclaimer = lazyWithRetry(
  () => import(/* webpackChunkName: "disclaimer" */ "../sections/Disclaimer"),
);
const ClubExplanationSection = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "club-explanation" */ "../sections/ClubExplanationSection"
    ),
);

export default function DesktopContainer({
  activity,
  activityWithEnrollableTimes,
  classNavTabs,
  leader,
  onClickMoreLikeThis,
  onClickViewReviews,
  onClickViewTeacher,
  refsContainer,
  setIsAvailabilityModalOpen,
  setTabValue,
  tabValue,
  viewAvailabilityRef,
}: DesktopContainerProps) {
  // wait for first render to load below-the-fold content
  const [isLoaded, setIsLoaded] = useState(false);
  const { mustPurchaseMembership } = useMembershipRequirement();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // If tutoringShowAvailableTimesInline is true, we show ActivityEnrollableTimes inline here.
  // Otherwise, tutoring times are shown in the enrollment modal instead.
  const { tutoringShowAvailableTimesInline } = useTutoringFlow();
  const showTutoringAvailableTimes =
    hasOneOnOneAvailabilities(activityWithEnrollableTimes) &&
    activity.canEnroll &&
    tutoringShowAvailableTimesInline;

  const showGroupClassAvailableTimes =
    activity.isSelfPaced || !(activity.isClub || activity.hasTeacherSchedule);

  const { headerHeight: mainHeaderHeight } = useStickyHeader();

  return (
    <Container
      sx={{
        display: "flex",
        marginY: "40px",
        alignItems: "flex-start",
      }}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          marginRight: "3em",
          maxWidth: "76rem",
          minWidth: "0", // override automatic minimum width
          flexDirection: "column",
          [theme.breakpoints.down("lg")]: {
            marginRight: "1.5em",
          },
        })}
        ref={refsContainer.root}
      >
        <Box ref={refsContainer.about.header}>
          <DesktopActivityHeader
            activity={activity}
            leader={leader}
            onClickViewTeacher={onClickViewTeacher}
            onClickViewReviews={onClickViewReviews}
          />
        </Box>
        <ClassDetailsNav tabs={classNavTabs ?? []} />
        <Box
          flex
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "3.2rem",
            gap: "9.6rem",
          }}
        >
          {!isLoaded ? (
            <Loading fullHeight />
          ) : (
            <>
              <Overview
                activity={activity}
                headerRef={refsContainer.about.overview}
              />
              <ActivityDetailDescription
                activity={activity}
                headerRef={refsContainer.about.classExperience}
              />
              <LearningGoals
                learningGoals={activity.learningGoals}
                headerRef={refsContainer.learningOverview.learningGoals}
              />
              <SyllabusSummary
                activity={activity}
                headerRef={refsContainer.learningOverview.syllabus}
              />
              <OtherDetails
                activity={activity}
                headerRef={refsContainer.learningOverview.otherDetails}
              />
              {activity.disclaimer && <Disclaimer text={activity.disclaimer} />}
              {showGroupClassAvailableTimes && (
                <Box ref={refsContainer.schedule.sections}>
                  <AvailableTimesWrapper
                    activity={activity}
                    onClickMoreLikeThis={onClickMoreLikeThis}
                  />
                </Box>
              )}
              {showTutoringAvailableTimes && (
                <Box ref={viewAvailabilityRef} sx={{ paddingY: 96 }}>
                  {activityWithEnrollableTimes && (
                    <ActivityEnrollableTimes
                      activity={activityWithEnrollableTimes}
                      leader={leader}
                    />
                  )}
                </Box>
              )}
              {activity.isClub && (
                <ClubExplanationSection activity={activity} />
              )}
              <TeacherSection
                activity={activity}
                leader={leader}
                onClickViewReviews={onClickViewReviews}
                teacherHeaderRef={refsContainer.teacher.header}
                credentialsRef={refsContainer.teacher.expertise}
              />
              {!!tabValue && !!setTabValue && (
                <ReviewsSection
                  activity={activity}
                  reviewsRef={refsContainer.reviews}
                  setTabValue={setTabValue}
                  tabValue={tabValue}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ position: "sticky", top: `${mainHeaderHeight + 40}px` }}>
        {mustPurchaseMembership ? (
          // Show this if logged out or if the user needs to purchase a membership
          <JoinMembershipDesktopActivityCard
            activity={activity}
            activityWithEnrollableTimes={activityWithEnrollableTimes}
            leader={leader}
            onClickMoreLikeThis={onClickMoreLikeThis}
          />
        ) : (
          // User has a membership or doesn't need one to enroll
          <UnlockedDesktopActivityCard
            activity={activity}
            activityWithEnrollableTimes={activityWithEnrollableTimes}
            leader={leader}
            onClickViewReviews={onClickViewReviews}
            onClickMoreLikeThis={onClickMoreLikeThis}
            sectionsRef={refsContainer.schedule.sections}
            viewAvailabilityRef={viewAvailabilityRef}
            setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
          />
        )}
      </Box>
    </Container>
  );
}
